import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import { useMatch, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { store } from "../../app/store";
import HeaderMenu from "../../layout/HeaderMenu";
import { Footer } from "../../screens/Footer/Footer";
import { AssistanceBoxComponent } from "../../ui/components/AssistanceBoxComponent";
import OrderDetailCard from "../../ui/components/MyOrders/OrderDetailCard";
import { OrderSummary } from "../../ui/components/MyOrders/OrderSummary";
import { TopBar } from "../../ui/components/TopBar/TopBar";
import { TwentyIcArrowLineLeft } from "../../ui/components/icons/TwentyIcArrowLineLeft";
import {
  MeetingCreateRequest,
  createMeetingAsync,
} from "../Meetings/MeetingSlice";
import { getFilteredUsersInfoPaginationAsync } from "../MyTeam/TeamUsersInfoSlice";
import { WellnessPsycho } from "../Wellness/WellnessSlice/WellnessPsychoSlice";
import {
  MyOrderDetail,
  getOrderAsync,
  myOrderDetailSelector,
} from "./MyOrderDetailSlice";
import "./OrderDetail.css";

export interface WellnessPsychoOrderDetailProps {}

const WellnessPsychoOrderDetail: React.FC<
  WellnessPsychoOrderDetailProps
> = () => {
  const navigate = useNavigate();
  const [id, setId] = useState<number>(-1);
  const [wellnessPsycho, setWellnessPsycho] = useState<WellnessPsycho>();
  const [showFullDescription, setShowFullDescription] = useState(false);

  const firstName = localStorage.getItem("firstName");
  const lastName = localStorage.getItem("lastName");
  const businessName = localStorage.getItem("businessName");

  const orderRoute = useMatch("wellness/psycho/myOrders/:id");

  let order: MyOrderDetail = useAppSelector(
    (state) =>
      myOrderDetailSelector.selectById(state, id) ?? ({} as MyOrderDetail)
  );

  useCalendlyEventListener({
    onEventScheduled: (e) => createMeetingFromEvent(e.data.event),
  });

  function createMeetingFromEvent(url: any) {
    const currentDate = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "Europe/Rome",
      hour12: false,
    };
    const italyTimeString = currentDate.toLocaleString("en-US", options);
    const meetingFromPayload: MeetingCreateRequest = {
      invoiceDate: new Date(italyTimeString),
      invoicePrice: order.wellnessPsycho?.price ?? 0,
      meetingInvoiceStatus: "NOT_INVOICED",
      meetingStatus: "NOT_APPROVED",
      bookingId: order.id ?? -1,
      providerId: order.wellnessPsycho?.userInfo.id ?? -1,
      tenantId: order.wellnessPsycho?.tenant.id ?? -1,
    };
    store.dispatch(createMeetingAsync({ meeting: meetingFromPayload }));
  }

  useEffect(() => {
    if (orderRoute !== null) {
      if (orderRoute.params.id) {
        setId(parseInt(orderRoute.params.id));
        store.dispatch(getOrderAsync(parseInt(orderRoute.params.id)));
        store.dispatch(getFilteredUsersInfoPaginationAsync());
        return;
      }
    }
  }, [orderRoute]);

  useEffect(() => {
    if (order.wellnessPsycho) {
      setWellnessPsycho(order.wellnessPsycho);
    }
  }, [order]);

  // Renderizza le informazioni del servizio e del fornitore
  const renderServiceInfo = () => {
    if (!wellnessPsycho) return null;

    const MAX_DESCRIPTION_LENGTH = 200;
    const description = wellnessPsycho.description || "";
    const shouldTruncate = description.length > MAX_DESCRIPTION_LENGTH;
    const displayedDescription = showFullDescription
      ? description
      : description.slice(0, MAX_DESCRIPTION_LENGTH);

    const providerName = wellnessPsycho.userInfo
      ? `${wellnessPsycho.userInfo.firstname} ${wellnessPsycho.userInfo.lastname}`
      : "N/A";

    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          marginTop: "20px",
          border: "1px solid #e5e5e5",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Nome Fornitore */}
        <div style={{ marginBottom: "16px" }}>
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: "#666",
              margin: "0 0 4px 0",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Fornitore
          </h3>
          <p
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#222729",
              margin: "0",
            }}
          >
            {providerName}
          </p>
        </div>

        {/* Nome Servizio */}
        <div style={{ marginBottom: "16px" }}>
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: "#666",
              margin: "0 0 4px 0",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Servizio
          </h3>
          <p
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#222729",
              margin: "0",
            }}
          >
            {wellnessPsycho.name}
          </p>
        </div>

        {/* Descrizione */}
        {description && (
          <div>
            <h3
              style={{
                fontSize: "14px",
                fontWeight: "700",
                color: "#666",
                margin: "0 0 8px 0",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Descrizione
            </h3>
            <p
              style={{
                fontSize: "16px",
                color: "#444",
                lineHeight: "1.6",
                margin: "0 0 12px 0",
                whiteSpace: "pre-wrap",
              }}
            >
              {displayedDescription}
              {shouldTruncate && !showFullDescription && "..."}
            </p>
            {shouldTruncate && (
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#36B39E",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  padding: "0",
                  textDecoration: "underline",
                }}
              >
                {showFullDescription ? "Mostra meno" : "Mostra altro"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {wellnessPsycho && (
        <>
          <HeaderMenu>
            <TopBar
              title={wellnessPsycho.name}
              hasEdit={false}
              hasDate={false}
              breadcrumbText={
                "Servizi per il benessere > I nostri psicologi > Dettaglio ordine"
              }
              icon={<TwentyIcArrowLineLeft className="icon-instance-node-3" />}
              onClick={() => navigate("/Wellness/Psycho/myOrders")}
            />
          </HeaderMenu>
          <div className="order-detail">
            <div className="order-detail-left-side">
              <OrderDetailCard
                id={order.id}
                date={order.bookingDate}
                price={wellnessPsycho.price}
                image={wellnessPsycho.image}
                status={order.status}
                personsNumber={order.numberOfPeople}
                isWellnessService={true}
                prenotazione={order.prenotazione}
              />
              {wellnessPsycho.link ? (
                <div className="calendly-inline-widget">
                  <InlineWidget url={wellnessPsycho.link} />
                </div>
              ) : null}

              {/* 📋 INFORMAZIONI SERVIZIO E FORNITORE */}
              {renderServiceInfo()}
            </div>
            <div className="order-detail-right-side">
              <OrderSummary
                price={wellnessPsycho.price}
                numberOfPeople={order.numberOfPeople}
                bookingStatus={order.status}
                isWellnessService={true}
              ></OrderSummary>
              <div className="summary-column">
                <AssistanceBoxComponent
                  assistance={t(
                    "smartPlanDetail.componentAssistantBox.assistance"
                  )}
                  whatsApp={t("smartPlanDetail.componentAssistantBox.whatsApp")}
                  date={t("smartPlanDetail.componentAssistantBox.date")}
                  whatsAppNumber={3773604135}
                  whatsAppMessageText={"Ciao, sono firstName* lastName* dell'azienda companyName*, ho bisogno di assistenza."
                    .replace("firstName*", firstName ?? "")
                    .replace("lastName*", lastName ?? "")
                    .replace("companyName*", businessName ?? "")}
                  disabled={undefined}
                />
              </div>
            </div>
          </div>
          <Footer />
        </>
      )}
    </>
  );
};

export default WellnessPsychoOrderDetail;
